import { useCallback, useEffect, useState, type FocusEvent } from 'react'
import { Trash2 } from 'lucide-react'
import {
  PROJECT_BILLING_SCHEMES,
  PROJECT_STATUSES,
  billingIntervalAllowed,
  billingIntervalsFor,
  erfuelltRecht,
  geldText,
} from '@symphenia/shared-types'
import { cardTitle, formCardSm, inlineInput, selectBase, textareaBase } from '@/lib/form-styles'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import {
  deleteProjectRegieRate,
  fetchNextDue,
  fetchPaymentPlan,
  fetchPhases,
  fetchProjectRegieRates,
  savePaymentPlan,
  setBillingDone,
  setProjectRegieRate,
  updateProject,
  updateProjectRegieRate,
  type NextDueAntwort,
  type RegieRatesAntwort,
} from '../api/projects-head.api'
import { BILLING_INTERVAL_LABELS, BILLING_SCHEME_LABELS, PROJECT_STATUS_LABELS, alsTag } from '../project-labels'
import type { PaymentPlanItem, ProjectDetail, ProjectPhase, ProjectRegieRate } from '../types/project.types'
import { useWarnungBeimVerlassen } from '@/lib/speichern'

/** Fehlertext der Abrechnungsmasken — kennt die Kopplung von Schema und Intervall. */
export function abrechnungsFehlerText(e: unknown): string {
  const code = (e as { response?: { data?: { code?: string } } })?.response?.data?.code
  if (code === 'PROJECT_BILLING_INTERVAL_MISMATCH') return 'Dieses Intervall passt nicht zum Abrechnungsschema.'
  if (code === 'REGIE_RATE_DUPLICATE') return 'Für diese Qualifikation gibt es schon einen Satz mit diesem Gültig-ab.'
  return apiErrorText(e, 'Das hat nicht geklappt.')
}

/** Satz aus dem Eingabefeld — Komma oder Punkt; leer oder unlesbar ist `null`. */
function satzAusText(text: string): number | null {
  const t = text.trim().replace(',', '.')
  if (t === '') return null
  const n = Number(t)
  return Number.isFinite(n) && n >= 0 ? n : null
}

function satzAlsText(rate: string | number | null | undefined): string {
  return rate === null || rate === undefined ? '' : String(rate).replace('.', ',')
}

/**
 * AP-35 Detailblatt 3 8.2 — **Reiter Abrechnung.**
 *
 * Drei Karten: Modalitäten (Schema, Intervall, Zusatzinfos, gerechneter
 * Stichtag, Freigabe „Abrechnung fertig"), Regiesätze (mit der Marke
 * „Mandant"/„eigen", direkt in der Tabelle bearbeitbar) und der Zahlungsplan —
 * der nur erscheint, wenn das Intervall „nach Zahlungsplan" lautet. Welches
 * Intervall zu welchem Schema passt, sagt `billingIntervalsFor` (shared-types).
 *
 * **Ohne den Baustein `.BILLING` sind die Karten sichtbar, aber schreibgeschützt
 * und nennen den Grund** (Blatt 3 10.) — eine fehlende Karte erklärt nichts.
 * Ohne Preisrecht bleiben die Sätze verborgen; der Server schickt sie nicht.
 */
export function ProjectBillingTab({ projekt, onChanged }: { projekt: ProjectDetail; onChanged: () => void }) {
  const { user, hasModule } = useAuthStore()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const abrechnung = hasModule('PROJECT_MGMT.BILLING')
  const darfSchreiben = abrechnung && darf('projects.sales.manage')
  const darfKopf = darf('projects.project.manage')
  const preise = darf('projects.price.view')

  const [saetze, setSaetze] = useState<RegieRatesAntwort | null>(null)
  const [plan, setPlan] = useState<PaymentPlanItem[]>([])
  const [stichtag, setStichtag] = useState<NextDueAntwort | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = useCallback(async () => {
    try {
      const [s, p, d] = await Promise.all([
        fetchProjectRegieRates(projekt.id),
        fetchPaymentPlan(projekt.id),
        fetchNextDue(projekt.id),
      ])
      setSaetze(s)
      setPlan(p)
      setStichtag(d)
    } catch (e) {
      setFehler(abrechnungsFehlerText(e))
    }
  }, [projekt.id])

  useEffect(() => {
    void laden()
  }, [laden])

  async function kopfAendern(felder: Record<string, unknown>) {
    try {
      setFehler(null)
      await updateProject(projekt.id, felder)
      onChanged()
      void laden()
    } catch (e) {
      setFehler(abrechnungsFehlerText(e))
    }
  }

  async function kopfSetzen(feld: string, wert: string) {
    await kopfAendern({ [feld]: wert || null })
  }

  // Schema und Intervall hängen zusammen (AP-35 Nachtrag 18.9.2026). Das Schema
  // kann vom Mandanten kommen — dann nennt es der Server im Stichtag mit.
  const wirksamesSchema = projekt.billingScheme ?? stichtag?.scheme ?? null
  const wirksamesIntervall = projekt.billingInterval ?? stichtag?.interval ?? null
  const intervalle = billingIntervalsFor(wirksamesSchema)

  /**
   * Schemawechsel: Passt das bisherige (auch geerbte) Intervall nicht mehr, geht
   * das erste erlaubte mit — beides in **einem** Aufruf, sonst lehnte der Server
   * den ersten Schritt ab. „Vorgabe des Mandanten" setzt beides zurück: Die
   * Vorgabe ist ein Paar.
   */
  async function schemaSetzen(wert: string) {
    if (!wert) return kopfAendern({ billingScheme: null, billingInterval: null })
    if (billingIntervalAllowed(wert, wirksamesIntervall)) return kopfAendern({ billingScheme: wert })
    return kopfAendern({ billingScheme: wert, billingInterval: billingIntervalsFor(wert)[0] ?? null })
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className={formCardSm}>
        <h3 className={cardTitle}>Modalitäten</h3>
        {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
        <div className="mt-3 space-y-3">
          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Schema</span>
            <select
              className={selectBase}
              disabled={!darfKopf}
              value={projekt.billingScheme ?? ''}
              onChange={(e) => void schemaSetzen(e.target.value)}
            >
              <option value="">— Vorgabe des Mandanten —</option>
              {PROJECT_BILLING_SCHEMES.map((s) => (
                <option key={s} value={s}>
                  {BILLING_SCHEME_LABELS[s]}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Intervall</span>
            <select
              className={selectBase}
              disabled={!darfKopf}
              value={projekt.billingInterval ?? ''}
              onChange={(e) => void kopfSetzen('billingInterval', e.target.value)}
            >
              {/* Die Vorgabe ist ein Paar: Nur wer das Schema erbt, erbt auch das Intervall. */}
              {(!projekt.billingScheme || !projekt.billingInterval) && <option value="">— Vorgabe des Mandanten —</option>}
              {projekt.billingInterval && !(intervalle as readonly string[]).includes(projekt.billingInterval) && (
                <option value={projekt.billingInterval} disabled>
                  {BILLING_INTERVAL_LABELS[projekt.billingInterval] ?? projekt.billingInterval} (passt nicht zum Schema)
                </option>
              )}
              {intervalle.map((i) => (
                <option key={i} value={i}>
                  {BILLING_INTERVAL_LABELS[i] ?? i}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">Zusatzinfos</span>
            <textarea
              className={textareaBase}
              rows={2}
              disabled={!darfKopf}
              defaultValue={projekt.billingNotes ?? ''}
              onBlur={(e) => void kopfSetzen('billingNotes', e.target.value)}
            />
          </label>

          <p className="text-sm text-muted-foreground">
            Nächster Stichtag: <span className="font-medium text-foreground">{alsTag(stichtag?.nextDueAt)}</span>
            {stichtag?.lastInvoiceNumber ? ` · letzte Rechnung ${stichtag.lastInvoiceNumber}` : ''}
          </p>

          {abrechnung && darf('projects.billing.run') && (
            <button
              type="button"
              className="rounded-md border border-border px-3 py-1.5 text-sm"
              onClick={async () => {
                try {
                  await setBillingDone(projekt.id)
                  onChanged()
                } catch (e) {
                  setFehler(abrechnungsFehlerText(e))
                }
              }}
            >
              Abrechnung als fertig freigeben
            </button>
          )}
        </div>
      </div>

      <div className={formCardSm}>
        <h3 className={cardTitle}>Regiesätze</h3>
        {!preise ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Sätze und Beträge sehen nur Benutzer mit dem Recht „Preise und Sätze sehen".
          </p>
        ) : (
          <RegieSaetze
            projekt={projekt}
            stand={saetze}
            darfSchreiben={darfSchreiben}
            abrechnungGebucht={abrechnung}
            onChanged={laden}
          />
        )}
      </div>

      {(wirksamesIntervall === 'PAYMENT_PLAN' || plan.length > 0) && (
        <div className={`${formCardSm} lg:col-span-2`}>
          <h3 className={cardTitle}>Zahlungsplan</h3>
          <Zahlungsplan
            projekt={projekt}
            plan={plan}
            darfSchreiben={darfSchreiben}
            abrechnungGebucht={abrechnung}
            onChanged={laden}
          />
        </div>
      )}
    </div>
  )
}

/**
 * Regiesätze direkt in der Tabelle (AP-35 Nachtrag 18.9.2026): Satz und
 * Gültig-ab werden beim Verlassen des Felds gespeichert; die leere Zeile am
 * Ende legt einen Satz an, sobald sie mit Qualifikation und Satz verlassen
 * wird. Ein Regiesatz hat keinen Belegbezug — die Verkaufszeile hält ihren
 * Preis selbst fest —, deshalb gibt es keine Sperre „abgerechnet".
 */
function RegieSaetze({
  projekt,
  stand,
  darfSchreiben,
  abrechnungGebucht,
  onChanged,
}: {
  projekt: ProjectDetail
  stand: RegieRatesAntwort | null
  darfSchreiben: boolean
  abrechnungGebucht: boolean
  onChanged: () => void
}) {
  const [fehler, setFehler] = useState<string | null>(null)

  if (!stand) return <p className="mt-2 text-sm text-muted-foreground">Lädt …</p>

  const zeilen = stand.qualifications.map((q) => {
    const eigen = stand.own.filter((r) => r.qualificationId === q.id)
    const vorgabe = stand.tenantDefaults.filter((r) => r.qualificationId === q.id)
    return { qualifikation: q, eigen, vorgabe }
  })

  async function ausfuehren(schritt: () => Promise<unknown>): Promise<boolean> {
    try {
      setFehler(null)
      await schritt()
      onChanged()
      return true
    } catch (e) {
      setFehler(abrechnungsFehlerText(e))
      return false
    }
  }

  return (
    <div className="mt-3 space-y-3">
      {!abrechnungGebucht && (
        <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Ändern gehört zum Baustein „Verkauf & Abrechnung" — er ist für diesen Betrieb nicht gebucht.
        </p>
      )}
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-1.5 pr-2">Qualifikation</th>
            <th className="py-1.5 pr-2">Satz</th>
            <th className="py-1.5 pr-2">gültig ab</th>
            <th className="py-1.5 pr-2">Herkunft</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {zeilen.map(({ qualifikation, eigen, vorgabe }) => {
            const gueltig = eigen.length > 0 ? eigen : vorgabe
            // Jede Zeile ist direkt bearbeitbar (Auftraggeber 18.9.2026): Wer bei einer
            // Vorgabe des Mandanten — oder wo noch kein Satz steht — etwas einträgt,
            // legt damit den eigenen Satz des Projekts an. Die Vorgabe bleibt unberührt.
            const zeilenSaetze = gueltig.length ? gueltig : [{ id: `ohne-${qualifikation.id}`, qualificationId: qualifikation.id, rate: null, validFrom: null }]
            return zeilenSaetze.map((satz) => (
              <RegieSatzZeile
                key={satz.id}
                satz={satz}
                name={qualifikation.name}
                bearbeitbar={darfSchreiben}
                loeschbar={darfSchreiben && Boolean((satz as ProjectRegieRate).projectId)}
                herkunft={(satz as ProjectRegieRate).projectId ? 'eigen' : gueltig.length ? 'Mandant' : undefined}
                leerHinweis={gueltig.length ? undefined : 'kein Satz hinterlegt'}
                onSpeichern={(felder) =>
                  (satz as ProjectRegieRate).projectId
                    ? ausfuehren(() => updateProjectRegieRate(projekt.id, satz.id, felder))
                    : ausfuehren(() =>
                        setProjectRegieRate(projekt.id, {
                          qualificationId: qualifikation.id,
                          rate: felder.rate ?? Number(satz.rate),
                          ...(felder.validFrom ? { validFrom: felder.validFrom } : {}),
                        }),
                      )
                }
                onLoeschen={() => void ausfuehren(() => deleteProjectRegieRate(projekt.id, satz.id))}
              />
            ))
          })}
          {darfSchreiben && (
            <NeueRegieSatzZeile
              mitHerkunft
              qualifikationen={stand.qualifications}
              onAnlegen={(felder) => ausfuehren(() => setProjectRegieRate(projekt.id, felder))}
            />
          )}
        </tbody>
      </table>
      {darfSchreiben && (
        <p className="text-xs text-muted-foreground">
          Satz und Gültig-ab direkt in der Tabelle ändern. Einen eigenen Satz legst du in der letzten Zeile an — er
          ersetzt die Vorgabe des Mandanten.
        </p>
      )}
    </div>
  )
}

/**
 * Eine Zeile der Regiesatz-Tabelle — geteilt von Projekt (hier) und
 * Mandantenvorgabe (Einstellungen, Qualifikationen). Gespeichert wird beim
 * Verlassen des Felds und nur, wenn sich etwas geändert hat; ein unlesbarer
 * Satz springt auf den gespeicherten Wert zurück.
 */
export function RegieSatzZeile({
  satz,
  name,
  bearbeitbar,
  loeschbar = bearbeitbar,
  herkunft,
  leerHinweis,
  onSpeichern,
  onLoeschen,
}: {
  satz: ProjectRegieRate
  name: string
  bearbeitbar: boolean
  /** Löschen nur, wo der Satz dieser Ebene gehört — Vorgabe: wie `bearbeitbar`. */
  loeschbar?: boolean
  /** Ohne Angabe keine Spalte „Herkunft" (Mandantenvorgabe). */
  herkunft?: 'eigen' | 'Mandant'
  /** Hinweis in der Herkunftsspalte, wenn noch kein Satz besteht. */
  leerHinweis?: string
  onSpeichern: (felder: { rate?: number; validFrom?: string | null }) => Promise<boolean>
  onLoeschen: () => void
}) {
  const gespeichertesDatum = satz.validFrom?.slice(0, 10) ?? ''
  const [rate, setRate] = useState(satzAlsText(satz.rate))
  const [validFrom, setValidFrom] = useState(gespeichertesDatum)

  useEffect(() => setRate(satzAlsText(satz.rate)), [satz.rate])
  useEffect(() => setValidFrom(gespeichertesDatum), [gespeichertesDatum])

  async function satzVerlassen() {
    const zahl = satzAusText(rate)
    if (zahl === null) return setRate(satzAlsText(satz.rate))
    if (satz.rate != null && zahl === Number(satz.rate)) return
    if (!(await onSpeichern({ rate: zahl }))) setRate(satzAlsText(satz.rate))
  }

  async function datumVerlassen() {
    if (validFrom === gespeichertesDatum) return
    if (!(await onSpeichern({ validFrom: validFrom || null }))) setValidFrom(gespeichertesDatum)
  }

  return (
    <tr className="border-b border-border/60">
      <td className="py-1.5 pr-2">{name}</td>
      <td className="py-1.5 pr-2">
        {bearbeitbar ? (
          <input
            className={`${inlineInput} w-24 text-right font-medium`}
            inputMode="decimal"
            aria-label={`Satz ${name}`}
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            onBlur={() => void satzVerlassen()}
          />
        ) : (
          <span className="font-medium">{satzAlsText(satz.rate) || '—'}</span>
        )}
      </td>
      <td className="py-1.5 pr-2">
        {bearbeitbar ? (
          <input
            type="date"
            className={inlineInput}
            aria-label={`gültig ab ${name}`}
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            onBlur={() => void datumVerlassen()}
          />
        ) : (
          alsTag(satz.validFrom)
        )}
      </td>
      {leerHinweis && !herkunft && <td className="py-1.5 pr-2 text-xs text-yellow-700 dark:text-yellow-300">{leerHinweis}</td>}
      {herkunft && (
        <td className="py-1.5 pr-2">
          <span
            className={`rounded-full px-1.5 py-0.5 text-xs ${
              herkunft === 'eigen' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
            }`}
          >
            {herkunft}
          </span>
        </td>
      )}
      <td className="py-1.5 text-right">
        {loeschbar && (
          <button
            type="button"
            className="text-muted-foreground hover:text-destructive"
            title="Satz löschen"
            onClick={onLoeschen}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </td>
    </tr>
  )
}

/**
 * Die leere Zeile am Ende: Angelegt wird, sobald der Fokus die Zeile verlässt
 * und Qualifikation und Satz ausgefüllt sind — kein eigener Knopf.
 */
export function NeueRegieSatzZeile({
  qualifikationen,
  mitHerkunft = false,
  onAnlegen,
}: {
  qualifikationen: { id: string; name: string }[]
  mitHerkunft?: boolean
  onAnlegen: (felder: { qualificationId: string; rate: number; validFrom?: string }) => Promise<boolean>
}) {
  const [neu, setNeu] = useState({ qualificationId: '', rate: '', validFrom: '' })
  const [laeuft, setLaeuft] = useState(false)

  async function zeileVerlassen(e: FocusEvent<HTMLTableRowElement>) {
    // Wechsel zwischen den Feldern derselben Zeile ist kein Verlassen.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
    const zahl = satzAusText(neu.rate)
    if (!neu.qualificationId || zahl === null || laeuft) return
    setLaeuft(true)
    const ok = await onAnlegen({ qualificationId: neu.qualificationId, rate: zahl, validFrom: neu.validFrom || undefined })
    setLaeuft(false)
    if (ok) setNeu({ qualificationId: '', rate: '', validFrom: '' })
  }

  return (
    <tr className="border-b border-border/60 bg-muted/20" onBlur={(e) => void zeileVerlassen(e)}>
      <td className="py-1.5 pr-2">
        <select
          className={inlineInput}
          aria-label="Qualifikation für neuen Satz"
          value={neu.qualificationId}
          onChange={(e) => setNeu((n) => ({ ...n, qualificationId: e.target.value }))}
        >
          <option value="">Qualifikation …</option>
          {qualifikationen.map((q) => (
            <option key={q.id} value={q.id}>
              {q.name}
            </option>
          ))}
        </select>
      </td>
      <td className="py-1.5 pr-2">
        <input
          className={`${inlineInput} w-24 text-right`}
          placeholder="neuer Satz"
          inputMode="decimal"
          value={neu.rate}
          onChange={(e) => setNeu((n) => ({ ...n, rate: e.target.value }))}
        />
      </td>
      <td className="py-1.5 pr-2">
        <input
          type="date"
          className={inlineInput}
          aria-label="gültig ab (neuer Satz)"
          value={neu.validFrom}
          onChange={(e) => setNeu((n) => ({ ...n, validFrom: e.target.value }))}
        />
      </td>
      {mitHerkunft && <td className="py-1.5 pr-2" />}
      <td />
    </tr>
  )
}

function zahlOderNull(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || String(v).trim() === '') return null
  const t = String(v).trim()
  // „1.250,00" (formatiert) wie „1250,5" (getippt) wie „1250.5" (vom Server) lesen.
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t)
  return Number.isFinite(n) ? n : null
}

/** 1250.5 → „1.250,50" — Tausenderpunkt und zwei Nachkommastellen (`geld.ts`). */
const alsWaehrung = geldText

function Zahlungsplan({
  projekt,
  plan,
  darfSchreiben,
  abrechnungGebucht,
  onChanged,
}: {
  projekt: ProjectDetail
  plan: PaymentPlanItem[]
  darfSchreiben: boolean
  abrechnungGebucht: boolean
  onChanged: () => void
}) {
  // Beträge vom Server gleich als Währungszahl zeigen.
  const formatiert = (p: PaymentPlanItem[]) => p.map((r) => ({ ...r, amount: r.amount == null || r.amount === '' ? r.amount : alsWaehrung(Number(r.amount)) }))
  const [raten, setRaten] = useState<PaymentPlanItem[]>(() => formatiert(plan))
  const [fehler, setFehler] = useState<string | null>(null)
  const [phasen, setPhasen] = useState<ProjectPhase[]>([])

  useEffect(() => {
    void fetchPhases().then((p) => setPhasen(p.filter((x) => x.isActive))).catch(() => undefined)
  }, [])

  useEffect(() => setRaten(formatiert(plan)), [plan])
  // AP-36 5.2: Ausnahme mit Knopf — ungespeichert warnt der Browser beim Schließen.
  useWarnungBeimVerlassen(JSON.stringify(raten) !== JSON.stringify(formatiert(plan)))

  const summe = raten.reduce((s, r) => s + Number(r.percent ?? 0), 0)

  return (
    <div className="mt-3 space-y-3">
      {!abrechnungGebucht && (
        <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Ändern gehört zum Baustein „Verkauf & Abrechnung".
        </p>
      )}
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th className="py-1.5 pr-2">Rate</th>
            <th className="py-1.5 pr-2">Auslöser</th>
            <th className="py-1.5 pr-2">Prozent</th>
            <th className="py-1.5 pr-2">Betrag</th>
            <th className="py-1.5 pr-2">abgerechnet</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {raten.map((rate, i) => (
            <tr key={rate.id ?? `neu-${i}`} className="border-b border-border/60">
              <td className="py-1.5 pr-2">
                <input
                  className={inlineInput}
                  disabled={!darfSchreiben || Boolean(rate.documentLineId)}
                  value={rate.label}
                  onChange={(e) =>
                    setRaten((r) => r.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))
                  }
                />
              </td>
              <td className="py-1.5 pr-2">
                <select
                  className={inlineInput}
                  disabled={!darfSchreiben || Boolean(rate.documentLineId)}
                  value={rate.trigger}
                  onChange={(e) => setRaten((r) => r.map((x, j) => (j === i ? { ...x, trigger: e.target.value } : x)))}
                >
                  <option value="MANUAL">von Hand</option>
                  <option value="DATE">Datum</option>
                  <option value="PROJECT_STATUS">Status</option>
                  <option value="PHASE_DONE">Phase fertig</option>
                </select>
                {/* Die Bedingung des Auslösers — ohne sie löste „Datum", „Status" oder „Phase" nie aus. */}
                {rate.trigger === 'DATE' && (
                  <input
                    type="date"
                    className={`${inlineInput} mt-1`}
                    aria-label="fällig am"
                    disabled={!darfSchreiben || Boolean(rate.documentLineId)}
                    value={rate.dueAt?.slice(0, 10) ?? ''}
                    onChange={(e) => setRaten((r) => r.map((x, j) => (j === i ? { ...x, dueAt: e.target.value || null } : x)))}
                  />
                )}
                {rate.trigger === 'PROJECT_STATUS' && (
                  <select
                    className={`${inlineInput} mt-1`}
                    aria-label="bei Status"
                    disabled={!darfSchreiben || Boolean(rate.documentLineId)}
                    value={rate.triggerStatus ?? ''}
                    onChange={(e) => setRaten((r) => r.map((x, j) => (j === i ? { ...x, triggerStatus: e.target.value || null } : x)))}
                  >
                    <option value="">— Status wählen —</option>
                    {PROJECT_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {PROJECT_STATUS_LABELS[s]?.label ?? s}
                      </option>
                    ))}
                  </select>
                )}
                {rate.trigger === 'PHASE_DONE' && (
                  <select
                    className={`${inlineInput} mt-1`}
                    aria-label="wenn Phase fertig"
                    disabled={!darfSchreiben || Boolean(rate.documentLineId)}
                    value={rate.triggerPhaseId ?? ''}
                    onChange={(e) => setRaten((r) => r.map((x, j) => (j === i ? { ...x, triggerPhaseId: e.target.value || null } : x)))}
                  >
                    <option value="">— Phase wählen —</option>
                    {phasen.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                )}
              </td>
              <td className="py-1.5 pr-2">
                <input
                  className={inlineInput}
                  inputMode="decimal"
                  disabled={!darfSchreiben || Boolean(rate.documentLineId)}
                  value={rate.percent ?? ''}
                  onChange={(e) =>
                    setRaten((r) => r.map((x, j) => (j === i ? { ...x, percent: e.target.value } : x)))
                  }
                />
              </td>
              <td className="py-1.5 pr-2">
                {/* Betrag als Währungszahl (1.250,00) — beim Tippen roh, beim Verlassen formatiert. */}
                <input
                  className={`${inlineInput} text-right tabular-nums`}
                  inputMode="decimal"
                  disabled={!darfSchreiben || Boolean(rate.documentLineId)}
                  value={rate.amount ?? ''}
                  onFocus={(e) => {
                    const n = zahlOderNull(e.target.value)
                    setRaten((r) => r.map((x, j) => (j === i ? { ...x, amount: n === null ? '' : String(n).replace('.', ',') } : x)))
                  }}
                  onBlur={(e) => {
                    const n = zahlOderNull(e.target.value)
                    setRaten((r) => r.map((x, j) => (j === i ? { ...x, amount: n === null ? null : alsWaehrung(n) } : x)))
                  }}
                  onChange={(e) => setRaten((r) => r.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))}
                />
              </td>
              <td className="py-1.5 pr-2 text-xs text-muted-foreground">{rate.documentLineId ? 'ja' : '—'}</td>
              <td className="py-1.5 text-right">
                {darfSchreiben && !rate.documentLineId && (
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={() => setRaten((r) => r.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className={`text-xs ${summe > 100 ? 'text-destructive' : 'text-muted-foreground'}`}>
        Summe der Prozente: {summe} % {summe > 100 && '— mehr als 100 % ist nicht möglich'}
      </p>

      {darfSchreiben && (
        <div className="flex justify-between">
          <button
            type="button"
            className="rounded-md border border-border px-3 py-1.5 text-sm"
            onClick={() => setRaten((r) => [...r, { label: '', trigger: 'MANUAL', percent: null, amount: null }])}
          >
            + Rate
          </button>
          <button
            type="button"
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground"
            onClick={async () => {
              try {
                await savePaymentPlan(
                  projekt.id,
                  raten.map((r) => ({
                    id: r.id,
                    label: r.label,
                    // 0 ist ein Wert, kein leeres Feld.
                    percent: zahlOderNull(r.percent),
                    amount: zahlOderNull(r.amount),
                    trigger: r.trigger,
                    // Die Bedingungen gehen immer mit — der Server schreibt `?? null`,
                    // ein fehlendes Feld löschte die gespeicherte Bedingung.
                    dueAt: r.trigger === 'DATE' ? (r.dueAt ?? null) : null,
                    triggerStatus: r.trigger === 'PROJECT_STATUS' ? (r.triggerStatus ?? null) : null,
                    triggerPhaseId: r.trigger === 'PHASE_DONE' ? (r.triggerPhaseId ?? null) : null,
                  })),
                )
                onChanged()
              } catch (e) {
                setFehler(abrechnungsFehlerText(e))
              }
            }}
          >
            Zahlungsplan speichern
          </button>
        </div>
      )}
    </div>
  )
}
